import React, {
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Grid,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataContext } from "../../App";
import useFetchData from "../Shared/FetchData/FetchData";
import { PageHeading } from "../Shared/PageHeading/PageHeading";
import SellerVolumeTable from "./SellerVolumeTable";

const weekColumns = [
	{ key: "week", label: "week" },
	{ key: "mon", label: "Mon" },
	{ key: "tue", label: "Tue" },
	{ key: "wed", label: "Wed" },
	{ key: "thu", label: "Thu" },
	{ key: "fri", label: "Fri" },
	{ key: "sat", label: "Sat" },
	{ key: "sun", label: "Sun" },
];

const averageWeekLabel = "avg last 6 complete weeks";

const weekDateFormatter = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
	year: "numeric",
	timeZone: "UTC",
});

export function formatWeekLabel(week) {
	if (!week || week === averageWeekLabel) {
		return week || "";
	}
	const date = new Date(`${String(week).slice(0, 10)}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) {
		return week;
	}
	return weekDateFormatter.format(date);
}

export function sortCreatedWeeks(rows) {
	const averages = [];
	const weeks = [];
	(rows || []).forEach((row) => {
		if (row.week === averageWeekLabel) {
			averages.push(row);
		} else {
			weeks.push(row);
		}
	});
	weeks.sort((left, right) => String(right.week).localeCompare(String(left.week)));
	return averages.concat(weeks);
}

export function formatCreateDate(value) {
	if (!value) {
		return "";
	}
	return String(value).slice(0, 10);
}

export function formatChannelDate(value) {
	return formatWeekLabel(formatCreateDate(value));
}

export function pivotOrdersPerChannel(rows) {
	const dates = [];
	const dateIndex = new Map();
	const channels = [];
	const seenChannels = new Set();

	(rows || []).forEach((row) => {
		const date = formatCreateDate(row.create_date);
		if (!dateIndex.has(date)) {
			dateIndex.set(date, true);
			dates.push(date);
		}
		if (!seenChannels.has(row.channel)) {
			seenChannels.add(row.channel);
			channels.push(row.channel);
		}
	});

	dates.sort((left, right) => right.localeCompare(left));
	dates.forEach((date, index) => {
		dateIndex.set(date, index);
	});

	const pivoted = dates.map((date) => {
		const record = { date: formatChannelDate(date) };
		channels.forEach((channel) => {
			record[channel] = "";
		});
		return record;
	});

	(rows || []).forEach((row) => {
		const date = formatCreateDate(row.create_date);
		pivoted[dateIndex.get(date)][row.channel] = row.nbrorders;
	});

	return {
		columns: ["date", ...channels],
		rows: pivoted,
	};
}

function ResultsTable({ title, subtitle, columns, rows, status, isLoading, onRefresh, pageSize, collapsible = false, expanded = false, onToggle }) {
	const [page, setPage] = useState(0);
	const shown = !collapsible || expanded;
	const hasError = status === false;
	useEffect(() => {
		setPage(0);
	}, [rows]);
	const visibleRows = pageSize ? rows.slice(page * pageSize, (page * pageSize) + pageSize) : rows;

	return (
		<Paper sx={{ p: 2, width: "100%", overflowX: "auto" }} elevation={2}>
			<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: shown ? 2 : 0 }}>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
					{collapsible ? (
						<IconButton
							aria-expanded={expanded}
							aria-label={`${expanded ? "Collapse" : "Expand"} ${title}`}
							onClick={onToggle}
							size="small"
						>
							{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					) : null}
					<Box>
						<Typography component="h2" variant="h6">{title}</Typography>
						{subtitle ? (
							<Typography variant="body2">{subtitle}</Typography>
						) : null}
					</Box>
				</Box>
				{shown ? (
					<Button
						aria-label={`Refresh ${title}`}
						onClick={onRefresh}
						disabled={isLoading}
						size="small"
						variant="outlined"
						startIcon={<RefreshIcon />}
					>
						Refresh
					</Button>
				) : null}
			</Box>
			<Collapse in={shown} unmountOnExit={collapsible}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.key} className="table-header">
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={Math.max(columns.length, 1)} align="center">
									<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
										<CircularProgress aria-label={`Loading ${title}`} />
									</Box>
								</TableCell>
							</TableRow>
						) : hasError ? (
							<TableRow>
								<TableCell colSpan={Math.max(columns.length, 1)} align="center">
									<Typography role="alert" color="error" sx={{ py: 3 }}>
										Unable to load this table. Please try again.
									</Typography>
								</TableCell>
							</TableRow>
						) : visibleRows.map((row, index) => (
							<TableRow key={index}>
								{columns.map((column) => (
									<TableCell key={column.key}>{row[column.key]}</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
				{pageSize && !isLoading && !hasError ? (
					<TablePagination
						component="div"
						count={rows.length}
						rowsPerPage={pageSize}
						rowsPerPageOptions={[]}
						page={page}
						onPageChange={(_, nextPage) => setPage(nextPage)}
					/>
				) : null}
			</Collapse>
		</Paper>
	);
}

export default function DailyOrdersSummary() {
	const context = useContext(DataContext);
	const [dashboardRefresh, setDashboardRefresh] = useState(0);
	const [ordersCreatedOpen, setOrdersCreatedOpen] = useState(false);
	const [ordersCreatedRefresh, setOrdersCreatedRefresh] = useState(0);
	const [ordersPerChannelOpen, setOrdersPerChannelOpen] = useState(false);
	const [ordersPerChannelRefresh, setOrdersPerChannelRefresh] = useState(0);
	const [ordersThisMonthOpen, setOrdersThisMonthOpen] = useState(false);
	const [ordersThisMonthRefresh, setOrdersThisMonthRefresh] = useState(0);
	const dashboard = useFetchData(useCallback(() =>
		context.dataProvider.getDailyOrdersDashboard(),
	[context.dataProvider, dashboardRefresh]));
	const loadOrdersCreated = useCallback(() =>
		context.dataProvider.getOrdersCreatedLast6Weeks(),
	[context.dataProvider, ordersCreatedRefresh]);
	const loadOrdersPerChannel = useCallback(() =>
		context.dataProvider.getOrdersPerChannel(),
	[context.dataProvider, ordersPerChannelRefresh]);
	const loadOrdersThisMonth = useCallback(() =>
		context.dataProvider.getOrdersDespatchedThisMonth(),
	[context.dataProvider, ordersThisMonthRefresh]);
	const ordersCreated = useFetchData(ordersCreatedOpen ? loadOrdersCreated : null);
	const ordersPerChannel = useFetchData(ordersPerChannelOpen ? loadOrdersPerChannel : null);
	const ordersThisMonth = useFetchData(ordersThisMonthOpen ? loadOrdersThisMonth : null);

	const channelTable = pivotOrdersPerChannel(
		Array.isArray(ordersPerChannel.results) ? ordersPerChannel.results : [],
	);

	return (
		<Box id="daily-orders-summary">
			<PageHeading pageTitle="Daily Orders Summary" />
			<Grid container spacing={2} sx={{ mt: 1 }}>
				<Grid item xs={12}>
					<ResultsTable
						title="Dashboard"
						status={dashboard.status}
						isLoading={dashboard.isLoading}
						onRefresh={() => setDashboardRefresh((value) => value + 1)}
						columns={[
							{ key: "period_label", label: "" },
							{ key: "created", label: "created" },
							{ key: "despatched", label: "despatched" },
						]}
						rows={Array.isArray(dashboard.results) ? dashboard.results : []}
					/>
				</Grid>
				<Grid item xs={12}>
					<ResultsTable
						title="Orders created per day last 6 weeks + current"
						subtitle="by create_date - includes cancelled"
						collapsible
						expanded={ordersCreatedOpen}
						onToggle={() => setOrdersCreatedOpen((value) => !value)}
						status={ordersCreated.status}
						isLoading={ordersCreated.isLoading}
						onRefresh={() => setOrdersCreatedRefresh((value) => value + 1)}
						columns={weekColumns}
						rows={sortCreatedWeeks(ordersCreated.results).map((row) => ({
							...row,
							week: formatWeekLabel(row.week),
						}))}
					/>
				</Grid>
				<Grid item xs={12}>
					<ResultsTable
						title="Orders per channel"
						collapsible
						expanded={ordersPerChannelOpen}
						onToggle={() => setOrdersPerChannelOpen((value) => !value)}
						status={ordersPerChannel.status}
						isLoading={ordersPerChannel.isLoading}
						onRefresh={() => setOrdersPerChannelRefresh((value) => value + 1)}
						columns={channelTable.columns.map((column) => ({
							key: column,
							label: column,
						}))}
						rows={channelTable.rows}
					/>
				</Grid>
				<Grid item xs={12}>
					<ResultsTable
						title="Orders despatched this month"
						collapsible
						expanded={ordersThisMonthOpen}
						onToggle={() => setOrdersThisMonthOpen((value) => !value)}
						status={ordersThisMonth.status}
						isLoading={ordersThisMonth.isLoading}
						onRefresh={() => setOrdersThisMonthRefresh((value) => value + 1)}
						pageSize={10}
						columns={[
							{ key: "client_short", label: "Client" },
							{ key: "nbrorders", label: "Number of Orders" },
						]}
						rows={Array.isArray(ordersThisMonth.results) ? ordersThisMonth.results : []}
					/>
				</Grid>
				<Grid item xs={12}>
					<SellerVolumeTable />
				</Grid>
			</Grid>
		</Box>
	);
}
